// Libs
import React from 'react';
import { withRouter } from 'react-router-dom';
import { Container, Form } from 'react-bootstrap';
import moment from 'moment';
import Select from 'react-select';

// Services & Helpers
import GlobalStateService from 'services/GlobalStateService';
import CourseService from 'services/CourseService';
import LanguageService from 'services/LanguageService';
import InstructorService from 'services/InstructorService';
import LocationService from 'services/LocationService';
import DataHelpers from 'helpers/DataHelpers';
import TextHelpers from 'helpers/TextHelpers';

// Components
import Loader from 'components/common/Loader';
import Enablement from 'components/enablement/Enablement';
import PageHeader from 'components/common/PageHeader';
import SideBar from 'components/common/SideBar';
import FormChecks from 'components/common/FormChecks';
import DatePicker from 'components/common/DatePicker';

//-------------------------------------------------------------------------------------------------------------------

class EnablementPage extends React.Component {

    constructor(props) {
        super(props);

        this.isFirstLoad = true;

        this.state = {
            isLoadingFilters: true,
            calendar: null,
            requestID: 0,
            isDirty: false,
            filters: {
                courseIDs: {},
                instructorIDs: {},
                statuses: {},
                activeStatuses: {},
                languageIDs: {},
                countryIDs: {},
                isApproved: true,
                isCandidate: false,
                statuses: {
                    approved: true,
                    ramping: true
                },
                activeStatuses: {
                    active: true
                },
                priorities: {
                    'true': true,
                    'false': true
                }
            }
        };
    }

    async componentDidMount() {
        GlobalStateService.setValues({ area: 'enablement' });
        window.scrollTo(0, 0);
        this.loadFilters();
    }

    async loadFilters() {
        this.setState({ isLoadingFilters: true });

        // Fire off all requests
        const coursesReq = CourseService.list({ ignoreDeprecated: true });
        const languagesReq = LanguageService.list();
        const geosReq = LocationService.listGeos();
        const countriesReq = LocationService.listCountries({ withInstructors: true });
        const instructorsReq = InstructorService.list({
            isApproved: true,
            isCandidate: false
        });

        // Gather responses
        const courses = await coursesReq;
        const geos = await geosReq;
        const languages = await languagesReq;
        const countries = await countriesReq;
        const instructors = await instructorsReq;

        // First load only - select everything
        if (this.isFirstLoad) {
            const filters = { ...this.state.filters };
            filters.courseIDs = DataHelpers.arrayToObject(courses);
            filters.geoIDs = DataHelpers.arrayToObject(geos);
            filters.languageIDs = DataHelpers.arrayToObject(languages);
            filters.countryIDs = DataHelpers.arrayToObject(countries);
            filters.instructorIDs = DataHelpers.arrayToObject(instructors);
            this.isFirstLoad = false;
            this.setState({ filters });
        }
        
        this.setState({
            courses,
            geos,
            languages,
            countries,
            instructors,
            isLoadingFilters: false
        });
    }

    async load() {
        this.setState({ isLoading: true, isDirty: false });

        // Load
        const enablement = await InstructorService.getEnablement(this.state.filters);

        // Create courses lookup for quicker rendering
        enablement.instructors.forEach(i => {
            const coursesLookup = {};
            i.courses.forEach(c => coursesLookup[c.courseID] = c);
            i.courses = coursesLookup;
        });

        // Update UI
        this.setState({
            enablement,
            isLoaded: true,
            requestID: this.state.requestID + 1,
            isLoading: false
        });
    }

    async exportToExcel() {
        await InstructorService.getEnablementExcel(this.state.filters);
    }

    updateFilter(name, value) {
        const filters = { ...this.state.filters };
        filters[name] = value;
        this.setState({
            filters,
            isDirty: true
        });
    }

    updateFilterOptions(filterName, values) {
        const filters = { ...this.state.filters };
        const filter = { ...filters[filterName] };
        for (let option in values) {
            const value = values[option];
            if (value) {
                filter[option] = value;
            } else {
                delete filter[option];
            }
        }
        filters[filterName] = filter;
        this.updateFilter(filterName, filter);
    }

    render() {
        const { isLoading } = this.state;
        const loginInfo = GlobalStateService.getValue('loginInfo');

        return (
            <div className="page enablement-page full-width-page">

                <PageHeader hasSideBar>
                    <h1>Enablement</h1>

                    {loginInfo.site.enableExcelExport &&
                        <div className="buttons">
                            <button className="btn ms-auto" onClick={e => this.exportToExcel()} disabled={isLoading}>
                                Export to Excel
                            </button>
                        </div>
                    }
                </PageHeader>

                <main>

                    <SideBar content={this.renderSideBarContent()} />

                    <Container fluid>
                        
                        <section>

                            {this.renderInner()}

                        </section>

                    </Container>

                </main>

            </div>
        );
    }

    renderSideBarContent() {
        const {
            isLoadingFilters,
            isLoading,
            filters,
            courses,
            instructors,
            geos,
            countries,
            languages,
            isDirty
        } = this.state;

        if (isLoadingFilters) {
            return (<Loader />);
        }

        const activeStatuses = [
            { id: 'active', name: 'Active' },
            { id: 'inactive', name: 'Inactive' },
            { id: 'potential', name: 'Potential' },
            { id: 'deactivated', name: 'Deactivated' },
            { id: 'suspended', name: 'Suspended' }
        ];

        const statuses = [
            { id: 'notApproved', name: 'Not approved' },
            { id: 'approved', name: 'Approved' },
            { id: 'ramping', name: 'Ramping' }
        ];
        
        const priorities = [
            { id: 'false', name: 'Non-priority' },
            { id: 'true', name: 'Priority' }
        ];

        // Get list of month options
        const months = [{ id: '', name: '(Any date)' }];
        let date = moment().startOf('month');
        for (var i = 0; i < 12; i++) {
            months.push({
                id: date.format('YYYY-MM-DD'),
                name: 'By ' + date.format('MMM YYYY')
            });
            date.add(1, 'month');
        }

        return (<>

            <form onSubmit={e => {
                e.preventDefault();
                this.load();
            }}>

                <Form.Group className="mb-3">
                    <Form.Label>Geo</Form.Label>
                    <FormChecks
                        isScrolling
                        allowSelectAllNone
                        id="geo"
                        options={geos}
                        value={filters.geoIDs}
                        onChange={(values) => this.updateFilterOptions('geoIDs', values)}
                    />
                </Form.Group>
            
                <Form.Group className="mb-3">
                    <Form.Label>Course</Form.Label>
                    <FormChecks
                        isScrolling
                        allowSelectAllNone
                        id="course"
                        options={courses}
                        value={filters.courseIDs}
                        onChange={(values) => this.updateFilterOptions('courseIDs', values)}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Enablement Status</Form.Label>
                    <FormChecks
                        isScrolling
                        allowSelectAllNone
                        id="statuses"
                        options={statuses}
                        value={filters.statuses}
                        onChange={(values) => this.updateFilterOptions('statuses', values)}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Active Status</Form.Label>
                    <FormChecks
                        isScrolling
                        allowSelectAllNone
                        id="active-status"
                        options={activeStatuses}
                        value={filters.activeStatuses}
                        onChange={(values) => this.updateFilterOptions('activeStatuses', values)}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Priority</Form.Label>
                    <FormChecks
                        isScrolling
                        allowSelectAllNone
                        id="priority"
                        options={priorities}
                        value={filters.priorities}
                        onChange={(values) => this.updateFilterOptions('priorities', values)}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Language</Form.Label>
                    <FormChecks
                        isScrolling
                        allowSelectAllNone
                        id="language"
                        options={languages}
                        value={filters.languageIDs}
                        onChange={(values) => this.updateFilterOptions('languageIDs', values)}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Country</Form.Label>
                    <FormChecks
                        isScrolling
                        allowSelectAllNone
                        id="country"
                        options={countries}
                        value={filters.countryIDs}
                        onChange={(values) => this.updateFilterOptions('countryIDs', values)}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Active Since...</Form.Label>
                    <DatePicker
                        className="form-control"
                        selected={filters.activeSinceDate}
                        onChange={value => this.updateFilter('activeSinceDate', value)}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Instructor</Form.Label>
                    <FormChecks
                        isScrolling
                        allowSelectAllNone
                        id="instructor"
                        options={instructors}
                        getLabel={i => TextHelpers.formatInstructorName(i)}
                        value={filters.instructorIDs}
                        onChange={(values) => this.updateFilterOptions('instructorIDs', values)}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>(Expected) Approval Date</Form.Label>
                    <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        value={filters.date ? months.find(m => m.id == moment(filters.date).format('YYYY-MM-DD')) : months.find(m => !m.id)}
                        options={months}
                        getOptionValue={i => i.id}
                        getOptionLabel={i => i.name}
                        onChange={v => this.updateFilter('date', v.id)}
                        placeholder="Ramping date"
                    />
                </Form.Group>

                <button type="submit" className="btn btn-primary w-100 mt-auto" disabled={isLoading}>
                    Load enablement {isDirty ? ' *' : ''}
                </button>

            </form>

        </>);
    }

    renderInner() {
        const {
            isLoading,
            isLoaded,
            enablement,
            requestID,
            filters
        } = this.state;

        if (isLoading) {
            return (<Loader />);
        }

        if (!isLoaded) {
            return (
                <div className="empty-text">
                    Please press 'Load enablement'.
                </div>
            );
        }

        return (<>

            <div className="sticky-table-container scrolling">
                
                <Enablement
                    requestID={requestID}
                    enablement={enablement}
                    statuses={filters.statuses}
                />

            </div>

        </>);
    }
}

export default withRouter(EnablementPage);